/**
 * A saved estimate: view, export, open for editing or delete
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
} from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { EmptyState, Page, PageHeader } from '../ui';
import EstimateResultView, { describeInputs } from '../estimate/EstimateResultView';
import { ModeChip } from '../estimate/EstimateResultPage';
import { pickBasics } from '../estimate/QuickEstimatePage';
import { getEstimateCopy } from '../estimate/copy';
import { useLanguage } from '../../utils/LanguageContext';
import { useEstimate } from '../../contexts/EstimateContext';
import { useGate } from '../../hooks/useGate';
import { GATE_ACTIONS } from '../../constants/gateConfig';
import { deleteProject, getProject } from '../../utils/projectStorage';
import { formatDate } from '../../utils/format';

const text = {
  en: {
    back: 'Projects',
    notFound: 'Project not found',
    notFoundDesc: 'It may have been deleted, or saved in another browser.',
    delete: 'Delete',
    deleteTitle: 'Delete this project?',
    deleteDesc: 'This removes the saved estimate from this device. It cannot be undone.',
    cancel: 'Cancel',
    open: 'Open & edit',
    saved: 'Saved',
  },
  my: {
    back: 'Projek',
    notFound: 'Projek tidak dijumpai',
    notFoundDesc: 'Ia mungkin telah dipadam, atau disimpan dalam pelayar lain.',
    delete: 'Padam',
    deleteTitle: 'Padam projek ini?',
    deleteDesc: 'Ini akan membuang anggaran yang disimpan daripada peranti ini. Tindakan ini tidak boleh dibatalkan.',
    cancel: 'Batal',
    open: 'Buka & ubah',
    saved: 'Disimpan',
  },
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = getEstimateCopy(language);
  const p = text[language] || text.en;
  const { setResult, setQuickDraft } = useEstimate();
  const { checkAccess, GateModalComponent } = useGate();
  const [project] = useState(() => getProject(projectId));
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!project) {
    return (
      <Page>
        <PageHeader title="" onBack={() => navigate('/app/projects')} backLabel={p.back} />
        <EmptyState
          icon={<SearchOffRoundedIcon />}
          title={p.notFound}
          description={p.notFoundDesc}
          action={<Button variant="contained" onClick={() => navigate('/app/projects')}>{p.back}</Button>}
        />
      </Page>
    );
  }

  const handleOpen = () => {
    // Make it the current estimate so it can be compared, edited or re-saved
    setResult(project);
    if (project.mode === 'quick') {
      setQuickDraft(pickBasics(project.inputs));
      navigate('/app/estimate/quick');
    } else {
      navigate('/app/estimate/detailed?from=result');
    }
  };

  const handlePdf = () => {
    if (!checkAccess(GATE_ACTIONS.EXPORT_PDF)) return;
    import('../../utils/pdfExport').then(({ generateEstimationPDF }) => generateEstimationPDF(project, language));
  };

  const handleDelete = () => {
    deleteProject(project.id);
    navigate('/app/projects', { replace: true });
  };

  return (
    <Page>
      <PageHeader
        overline={<ModeChip mode={project.mode} t={t} />}
        title={project.inputs.projectName || t.resultTitle}
        subtitle={`${describeInputs(project.inputs, t, language)} · ${p.saved} ${formatDate(project.savedAt, language)}`}
        onBack={() => navigate('/app/projects')}
        backLabel={p.back}
      />

      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={5}>
          <Button fullWidth variant="contained" startIcon={<EditOutlinedIcon />} onClick={handleOpen}>
            {p.open}
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button fullWidth variant="outlined" startIcon={<PictureAsPdfOutlinedIcon />} onClick={handlePdf}>
            PDF
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button fullWidth variant="outlined" color="error" onClick={() => setConfirmOpen(true)} aria-label={p.delete}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </Button>
        </Grid>
      </Grid>

      <EstimateResultView estimate={project} t={t} language={language} />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{p.deleteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{p.deleteDesc}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>{p.cancel}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            {p.delete}
          </Button>
        </DialogActions>
      </Dialog>

      <GateModalComponent />
    </Page>
  );
};

export default ProjectDetailPage;
